import type { LocationOption } from '../types';

interface Province {
  id?: string;
  code?: string;
  name: string;
}

interface City {
  id?: string;
  code?: string;
  name: string;
  province_code?: string;
  province_id?: string;
}

interface District {
  id?: string;
  code?: string;
  name: string;
  regency_code?: string;
  regency_id?: string;
}

const CACHE_KEYS = {
  PROVINCES: 'wilayah_provinces',
  CITIES: 'wilayah_cities_',
  DISTRICTS: 'wilayah_districts_',
};

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const REQUEST_TIMEOUT = 10000; // 10 seconds

class WilayahService {
  // Using emsifa.com API (correct endpoint)
  private baseUrl = 'https://www.emsifa.com/api-wilayah-indonesia/api';
  // Fallback to wilayah.id
  private fallbackUrl = 'https://wilayah.id/api';

  /**
   * Fetch with timeout support
   */
  private async fetchWithTimeout(url: string, timeout: number = REQUEST_TIMEOUT): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - please try again or use manual input');
      }
      throw error;
    }
  }

  /**
   * Get cached data from localStorage
   */
  private getCachedData<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid
      if (now - timestamp < CACHE_DURATION) {
        return data as T;
      }

      // Cache expired, remove it
      localStorage.removeItem(key);
      return null;
    } catch (error) {
      console.error('Error reading cache:', error);
      return null;
    }
  }

  /**
   * Save data to localStorage cache
   */
  private setCachedData<T>(key: string, data: T): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  }

  /**
   * Fetch provinces from API with fallback
   */
  async getProvinces(): Promise<LocationOption[]> {
    const cacheKey = CACHE_KEYS.PROVINCES;

    // Try to get from cache first
    const cached = this.getCachedData<LocationOption[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Try primary API first
      let response = await this.fetchWithTimeout(`${this.baseUrl}/provinces.json`);
      
      if (!response.ok) {
        // Try fallback API
        response = await this.fetchWithTimeout(`${this.fallbackUrl}/provinces.json`);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both API formats
      let provinces: Province[] = [];
      if (Array.isArray(data)) {
        provinces = data;
      } else if (data.data && Array.isArray(data.data)) {
        provinces = data.data;
      }

      const locationOptions: LocationOption[] = provinces.map(province => ({
        id: province.id || province.code || province.name,
        name: province.name,
      }));

      // Cache the results
      this.setCachedData(cacheKey, locationOptions);

      return locationOptions;
    } catch (error) {
      console.error('Error fetching provinces:', error);

      // Try to return cached data even if expired
      const expiredCache = this.getCachedData<LocationOption[]>(cacheKey);
      if (expiredCache) {
        console.warn('Using expired cache for provinces');
        return expiredCache;
      }

      throw new Error(
        'Unable to load provinces. Please check your connection or use manual text input.'
      );
    }
  }

  /**
   * Fetch cities for a given province
   */
  async getCities(provinceId: string): Promise<LocationOption[]> {
    if (!provinceId) {
      throw new Error('Province ID is required');
    }

    const cacheKey = `${CACHE_KEYS.CITIES}${provinceId}`;

    // Try to get from cache first
    const cached = this.getCachedData<LocationOption[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Try primary API first
      let response = await this.fetchWithTimeout(
        `${this.baseUrl}/regencies/${provinceId}.json`
      );
      
      if (!response.ok) {
        // Try fallback API
        response = await this.fetchWithTimeout(
          `${this.fallbackUrl}/regencies/${provinceId}.json`
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both API formats
      let cities: City[] = [];
      if (Array.isArray(data)) {
        cities = data;
      } else if (data.data && Array.isArray(data.data)) {
        cities = data.data;
      }

      const locationOptions: LocationOption[] = cities.map(city => ({
        id: city.id || city.code || city.name,
        name: city.name,
      }));

      // Cache the results
      this.setCachedData(cacheKey, locationOptions);

      return locationOptions;
    } catch (error) {
      console.error('Error fetching cities:', error);

      // Try to return cached data even if expired
      const expiredCache = this.getCachedData<LocationOption[]>(cacheKey);
      if (expiredCache) {
        console.warn('Using expired cache for cities');
        return expiredCache;
      }

      throw new Error(
        'Unable to load cities. Please check your connection or use manual text input.'
      );
    }
  }

  /**
   * Fetch districts for a given city
   */
  async getDistricts(cityId: string): Promise<LocationOption[]> {
    if (!cityId) {
      throw new Error('City ID is required');
    }

    const cacheKey = `${CACHE_KEYS.DISTRICTS}${cityId}`;

    // Try to get from cache first
    const cached = this.getCachedData<LocationOption[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Try primary API first
      let response = await this.fetchWithTimeout(
        `${this.baseUrl}/districts/${cityId}.json`
      );
      
      if (!response.ok) {
        // Try fallback API
        response = await this.fetchWithTimeout(
          `${this.fallbackUrl}/districts/${cityId}.json`
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both API formats
      let districts: District[] = [];
      if (Array.isArray(data)) {
        districts = data;
      } else if (data.data && Array.isArray(data.data)) {
        districts = data.data;
      }

      const locationOptions: LocationOption[] = districts.map(district => ({
        id: district.id || district.code || district.name,
        name: district.name,
      }));

      // Cache the results
      this.setCachedData(cacheKey, locationOptions);

      return locationOptions;
    } catch (error) {
      console.error('Error fetching districts:', error);

      // Try to return cached data even if expired
      const expiredCache = this.getCachedData<LocationOption[]>(cacheKey);
      if (expiredCache) {
        console.warn('Using expired cache for districts');
        return expiredCache;
      }

      throw new Error(
        'Unable to load districts. Please check your connection or use manual text input.'
      );
    }
  }

  /**
   * Clear all cached wilayah data
   */
  clearCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('wilayah_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}

// Export singleton instance
export const wilayahService = new WilayahService();
export default wilayahService;
